// Enhanced Service Worker Registration Utility
// Handles registration, updates, aggressive caching, and offline capabilities

export const swConfig = {
  onSuccess: null,
  onUpdate: null,
  onOffline: null,
  onOnline: null,
  enablePreloading: true,
  enableBackgroundSync: true,
};

// Check if service worker is supported
export const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

// Register service worker
export const registerServiceWorker = async (config = {}) => {
  if (!isServiceWorkerSupported()) {
    console.log('Service Worker not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('Service Worker registered successfully:', registration);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      handleUpdateFound(registration, config);
    });

    // Check for existing service worker
    if (registration.active) {
      console.log('Service Worker is active');
      config.onSuccess?.(registration);
    }

    // Handle controller changes
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker controller changed');
      window.location.reload();
    });

    // Handle messages from service worker
    navigator.serviceWorker.addEventListener('message', event => {
      handleServiceWorkerMessage(event, config);
    });

    // Check for updates periodically (but clean up to avoid bfcache issues)
    const updateInterval = setInterval(() => {
      registration.update();
    }, 300000); // Check every 5 minutes (less frequent to avoid bfcache issues)

    // Clean up interval on page hide to enable bfcache
    const cleanupUpdateInterval = () => {
      clearInterval(updateInterval);
    };

    window.addEventListener('pagehide', cleanupUpdateInterval);
    window.addEventListener('beforeunload', cleanupUpdateInterval);

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
};

// Handle service worker updates
const handleUpdateFound = (registration, config) => {
  const installingWorker = registration.installing;

  if (!installingWorker) {
    return;
  }

  console.log('Service Worker update found');

  installingWorker.addEventListener('statechange', () => {
    if (installingWorker.state === 'installed') {
      if (navigator.serviceWorker.controller) {
        // New content available
        console.log('New content available, update available');
        config.onUpdate?.(registration);

        // Show update notification
        showUpdateNotification(registration);
      } else {
        // Content cached for first time
        console.log('Content cached for offline use');
        config.onSuccess?.(registration);
      }
    }
  });
};

// Handle messages from service worker
const handleServiceWorkerMessage = (event, config) => {
  const { data } = event;

  switch (data.type) {
    case 'FORM_SYNC_SUCCESS':
      console.log('Form submission synced successfully');
      showNotification('Form submitted successfully!', 'success');
      break;

    case 'CACHE_UPDATED':
      console.log('Cache updated');
      break;

    case 'OFFLINE_READY':
      console.log('App ready for offline use');
      config.onOffline?.();
      break;

    default:
      console.log('Unknown message from service worker:', data);
  }
};

// Show update notification
const showUpdateNotification = registration => {
  const updateNotification = document.createElement('div');
  updateNotification.className = 'sw-update-notification';
  updateNotification.innerHTML = `
    <div class="sw-update-content">
      <span>New version available!</span>
      <button class="sw-update-button" onclick="window.updateServiceWorker()">Update</button>
      <button class="sw-update-dismiss" onclick="this.parentElement.parentElement.remove()">×</button>
    </div>
  `;

  document.body.appendChild(updateNotification);

  // Set up update function
  window.updateServiceWorker = () => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    updateNotification.remove();
  };

  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    if (updateNotification.parentElement) {
      updateNotification.remove();
    }
  }, 10000);
};

// Show general notification
const showNotification = (message, type = 'info') => {
  const notification = document.createElement('div');
  notification.className = `sw-notification sw-notification--${type}`;
  notification.textContent = message;

  document.body.appendChild(notification);

  setTimeout(() => {
    if (notification.parentElement) {
      notification.remove();
    }
  }, 3000);
};

// Check if app is running as PWA
export const isRunningAsPWA = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone ||
    document.referrer.includes('android-app://')
  );
};

// Send message to service worker
export const sendMessageToServiceWorker = async message => {
  if (!isServiceWorkerSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration && registration.active) {
      registration.active.postMessage(message);
    }
  } catch (error) {
    console.error('Failed to send message to service worker:', error);
  }
};

// Preload critical assets for aggressive caching
export const preloadCriticalAssets = async () => {
  if (!isServiceWorkerSupported()) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;

    // Discover critical assets from the current page
    const criticalAssets = [];

    // Get all CSS files
    document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      if (link.href && link.href.includes('/static/css/')) {
        criticalAssets.push(link.href);
      }
    });

    // Get all JS files from script tags
    document.querySelectorAll('script[src]').forEach(script => {
      if (script.src && script.src.includes('/static/js/')) {
        criticalAssets.push(script.src);
      }
    });

    // Get font files
    document.querySelectorAll('link[rel="preload"][as="font"]').forEach(link => {
      if (link.href) {
        criticalAssets.push(link.href);
      }
    });

    if (criticalAssets.length > 0) {
      console.log('SW: Preloading', criticalAssets.length, 'critical assets');

      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = event => {
        if (event.data?.preloaded) {
          console.log('SW: Critical assets preloaded successfully');
        }
      };

      registration.active?.postMessage(
        { type: 'PRELOAD_ASSETS', assets: criticalAssets },
        [messageChannel.port2]
      );
    }
  } catch (error) {
    console.error('Failed to preload critical assets:', error);
  }
};

// Get cache statistics
export const getCacheStatistics = async () => {
  if (!isServiceWorkerSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.active) return null;

    return new Promise(resolve => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = event => {
        resolve(event.data?.stats || null);
      };

      registration.active.postMessage({ type: 'GET_CACHE_STATS' }, [
        messageChannel.port2,
      ]);

      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
    });
  } catch (error) {
    console.error('Failed to get cache statistics:', error);
    return null;
  }
};

// Clear all caches
export const clearAllCaches = async () => {
  if (!isServiceWorkerSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.active) return false;

    return new Promise(resolve => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = event => {
        resolve(event.data?.success || false);
      };

      registration.active.postMessage({ type: 'CLEAR_CACHE' }, [messageChannel.port2]);

      setTimeout(() => resolve(false), 10000);
    });
  } catch (error) {
    console.error('Failed to clear caches:', error);
    return false;
  }
};

// Enhanced network status handling
const setupNetworkHandling = config => {
  let isOnline = navigator.onLine;

  const handleOnline = () => {
    if (!isOnline) {
      isOnline = true;
      console.log('Network connection restored');
      config.onOnline?.();
      showNotification('Connection restored', 'success');

      // Trigger background sync for any pending submissions
      if (
        'serviceWorker' in navigator &&
        'sync' in window.ServiceWorkerRegistration.prototype
      ) {
        navigator.serviceWorker.ready
          .then(registration => {
            return registration.sync.register('contact-form');
          })
          .catch(error => {
            console.log('Background sync registration failed:', error);
          });
      }
    }
  };

  const handleOffline = () => {
    if (isOnline) {
      isOnline = false;
      console.log('Network connection lost');
      config.onOffline?.();
      showNotification(
        'You are offline. Form submissions will be synced when connection is restored.',
        'info'
      );
    }
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Check connection quality
  if ('connection' in navigator) {
    const connection = navigator.connection;

    const logConnectionInfo = () => {
      console.log(
        `SW: Connection - ${connection.effectiveType}, ${connection.downlink}Mbps`
      );

      // Adjust caching strategy based on connection
      if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
        // More aggressive caching for slow connections
        sendMessageToServiceWorker({ type: 'ENABLE_AGGRESSIVE_CACHING' });
      }
    };

    connection.addEventListener('change', logConnectionInfo);
    logConnectionInfo();
  }
};

// Initialize service worker with enhanced configuration
export const initializeServiceWorker = async (config = {}) => {
  // Set up enhanced network status handling
  setupNetworkHandling(config);

  // Register service worker
  const registration = await registerServiceWorker(config);

  // Preload critical assets after registration
  if (config.enablePreloading !== false) {
    setTimeout(() => {
      preloadCriticalAssets().catch(error => {
        console.error('Critical asset preloading failed:', error);
      });
    }, 2000); // Delay to avoid blocking initial page load
  }

  // Set up periodic cache cleanup with proper cleanup for bfcache
  const cacheStatsInterval = setInterval(async () => {
    try {
      const stats = await getCacheStatistics();
      if (stats) {
        const totalCached = Object.values(stats).reduce((sum, count) => sum + count, 0);
        console.log(`SW: Cache statistics - ${totalCached} total items cached`);

        // Log cache efficiency
        if (totalCached > 500) {
          console.log('SW: Large cache detected, consider cleanup');
        }
      }
    } catch (error) {
      // Ignore errors in background tasks
    }
  }, 600000); // Check every 10 minutes (less frequent for bfcache)

  // Clean up intervals on page hide to enable bfcache
  const cleanupIntervals = () => {
    clearInterval(cacheStatsInterval);
  };

  window.addEventListener('pagehide', cleanupIntervals);
  window.addEventListener('beforeunload', cleanupIntervals);

  return registration;
};

export default {
  initializeServiceWorker,
  registerServiceWorker,
  isServiceWorkerSupported,
  isRunningAsPWA,
  sendMessageToServiceWorker,
  preloadCriticalAssets,
  getCacheStatistics,
  clearAllCaches,
};
